'use client'

import {useEffect, useEffectEvent, useState, type ReactNode} from 'react'
import Link from 'next/link'
import {Loader2} from 'lucide-react'
import {Modal} from '../../ui/Modal'
import {useTranslation} from '../../../hooks/useTranslation'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {getPublicEventGuestCheckoutHref} from './publicEventLinks'
import {GuestBenefitCardChoice} from './GuestBenefitCardChoice'
import {getButtonClasses} from '@fitro/customer-ui/Button'
import {trackGuestCheckoutChooserOpened} from '../../../lib/guest-checkout-journey'
import {publicFontClassName} from '../../../lib/public-font'
import {PublicTermDetails, usePublicTermSession} from './PublicTermDetails'

const PRIMARY_CLASS_NAME = getButtonClasses({variant: 'primary', className: 'w-full gap-2'})
const SECONDARY_CLASS_NAME = getButtonClasses({variant: 'secondary', className: 'w-full'})

/**
 * Why booking without an account did not go through, and the one step still open to the visitor:
 * signing in (the Term needs an account, or its waitlist does), another Term, or trying again.
 */
export type GuestBookingFailure = {message: string; next: 'sign_in' | 'other_terms' | 'retry'; waitlist?: boolean}

/**
 * A signed-out visitor's booking action: for a full class with a waitlist, signing in to join it;
 * otherwise booking without an account, which `direct` starts at once and which opens a choice only
 * for a benefit card price or a `failure`. `onGuest` starts the Guest Checkout: the choice stays
 * open while it is `pending` and shows a `failure` with its next step instead of the guest option.
 * `onClose` tells the caller the dialog was closed; `defaultOpen` opens the choice on arrival. With
 * `onBenefitCardChange` a guest who can show a benefit card picks the price (`benefitCardKey`:
 * `provider` or `provider:variant`, null for the regular one).
 */
export function PublicGuestBookingChooser({
    benefitCardKey = null,
    children,
    className,
    defaultOpen = false,
    direct = false,
    disabled,
    event,
    failure = null,
    mode,
    onClose,
    onBenefitCardChange,
    onGuest,
    openRequest = 0,
    otherTermsHref,
    pending = false,
    signInHref,
}: {
    benefitCardKey?: string | null
    children: ReactNode
    className: string
    defaultOpen?: boolean
    /** Starts `onGuest` on click instead of opening the choice; a failure still opens it. */
    direct?: boolean
    disabled?: boolean
    event: PublicScheduleEvent
    failure?: GuestBookingFailure | null
    mode: 'guest' | 'waitlist'
    onClose?: () => void
    onBenefitCardChange?: (cardKey: string | null) => void
    onGuest?: () => void
    /** Each increase opens the choice, for a booking started in another window (the details window). */
    openRequest?: number
    otherTermsHref?: string
    pending?: boolean
    signInHref: string
}) {
    const {t} = useTranslation('landing')
    const term = usePublicTermSession(event)
    const [open, setOpen] = useState(false)
    const guestHref = mode === 'guest' ? getPublicEventGuestCheckoutHref(event) : null
    const close = () => {
        setOpen(false)
        onClose?.()
    }
    const openChooser = () => {
        setOpen(true)
        if (mode === 'guest') trackGuestCheckoutChooserOpened()
    }
    const openOnArrival = useEffectEvent(() => { if (defaultOpen) openChooser() })
    useEffect(() => { openOnArrival() }, [defaultOpen])
    const openOnRequest = useEffectEvent(() => { if (openRequest > 0) openChooser() })
    useEffect(() => { openOnRequest() }, [openRequest])
    const guestClosed = failure?.next === 'sign_in' || failure?.next === 'other_terms'
    const cardChoice = guestHref && !guestClosed && onBenefitCardChange && (event.guestCheckoutPrice?.benefitCards ?? []).length > 0
        ? event.guestCheckoutPrice
        : null
    // Next to booking without an account, the account path only needs signing in.
    const signInLink = (primary: boolean) => (
        <Link href={signInHref} onClick={close} className={primary ? PRIMARY_CLASS_NAME : SECONDARY_CLASS_NAME}>
            {t(primary ? 'publicEntry.guestCheckout.signIn' : 'publicEntry.auth.signIn')}
        </Link>
    )

    return (
        <>
            {direct ? (
                // Booking starts here: the button shows the availability check until the form replaces it.
                <button type="button" className={className} disabled={disabled || pending} aria-busy={pending} onClick={onGuest}>
                    {pending ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" aria-hidden="true" /> : null}
                    {pending ? t('publicEntry.guestCheckout.loading') : children}
                </button>
            ) : (
                <button type="button" aria-haspopup="dialog" className={className} disabled={disabled} onClick={openChooser}>
                    {children}
                </button>
            )}
            {/* The same width, title and facts as the details window, so the visitor recognises the Term. */}
            <Modal
                isOpen={open}
                onClose={close}
                size="medium"
                title={event.className?.trim() || t('publicEntry.events.fallbackTitle')}
                description={term.label}
                className={publicFontClassName}
            >
                <div className="space-y-4">
                    {/* A guest who can show a benefit card picks the price below instead of reading it in the facts. */}
                    <PublicTermDetails event={event} price={cardChoice ? null : undefined}/>
                    {cardChoice && onBenefitCardChange ? <GuestBenefitCardChoice price={cardChoice} selectedCardKey={benefitCardKey} onSelect={onBenefitCardChange} disabled={pending} /> : null}
                    {failure ? (
                        <p role="alert" className={failure.waitlist ? 'text-[14px] text-[var(--landing-neutral-600)]' : 'text-[14px] text-red-700'}>
                            {failure.message}
                        </p>
                    ) : null}
                    {guestHref && guestClosed ? (
                        <>
                            {failure?.next === 'sign_in' || failure?.waitlist ? signInLink(true) : null}
                            {failure?.next === 'other_terms' && otherTermsHref ? (
                                <Link href={otherTermsHref} onClick={close} className={SECONDARY_CLASS_NAME}>
                                    {t('publicEntry.guestCheckout.otherTerms')}
                                </Link>
                            ) : null}
                        </>
                    ) : guestHref ? (
                        <>
                            <button type="button" disabled={pending} onClick={onGuest} className={PRIMARY_CLASS_NAME}>
                                {pending ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" aria-hidden="true" /> : null}
                                {t(pending ? 'publicEntry.guestCheckout.loading' : 'publicEntry.guestCheckout.reserve')}
                            </button>
                            <p className="border-t border-[var(--landing-neutral-100)] pt-4 text-center text-[14px] font-medium text-[var(--landing-neutral-700)]">{t('publicEntry.guestCheckout.chooserAccountHint')}</p>
                            {signInLink(false)}
                        </>
                    ) : (
                        <>
                            <p className="text-[14px] text-[var(--landing-neutral-600)]">{t('publicEntry.guestCheckout.fullWaitlist')}</p>
                            {signInLink(true)}
                        </>
                    )}
                </div>
            </Modal>
        </>
    )
}
